import { Markdown } from "#root/components/markdown";
import Sidebar from "#root/components/sidebar";
import { WithSidebar } from "#root/components/withSidebar";
import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import PageLayout from "../layout";
import { type DocPage, docSections, getNeighbors } from "./content";

type DocsLayoutProps = {
  page?: DocPage;
  markdown?: string;
  children?: ReactNode;
};

export default function DocsLayout({ page, markdown, children }: DocsLayoutProps): ReactNode {
  const neighbors = page ? getNeighbors(page.slug) : {};

  return (
    <PageLayout title={page ? `${page.title} — CloudQuote Docs` : "CloudQuote Documentation"} description={page?.description ?? ""}>
      <WithSidebar>
        <Sidebar>
          <nav className="docs-sidebar p-4">
            <Link to="/docs" className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 no-underline hover:text-gray-600">
              Documentation
            </Link>
            {docSections.map(section => (
              <div key={section.label} className="mb-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 m-0">{section.label}</h3>
                <ul className="m-0 p-0 list-none space-y-0.5">
                  {section.pages.map(p => (
                    <li key={p.slug}>
                      <NavLink
                        to={p.path}
                        end={p.end}
                        className={({ isActive }) =>
                          `block px-3 py-1.5 text-sm rounded transition-colors no-underline border-l-2 ${
                            isActive
                              ? "bg-blue-50 text-blue-700 border-blue-500 font-medium"
                              : "text-gray-700 hover:bg-gray-100 border-transparent"
                          }`
                        }
                      >
                        {p.title}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </Sidebar>
        <section>
          <article className="max-w-3xl mx-auto px-2 py-8">
            {page ? (
              <header className="mb-8 pb-4 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 m-0">{page.title}</h1>
                <p className="text-gray-500 text-base m-0">{page.description}</p>
              </header>
            ) : null}
            {markdown ? <Markdown markdown={markdown}/> : null}
            {children}
            {page ? <DocsFooter prev={neighbors.prev} next={neighbors.next}/> : null}
          </article>
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function DocsFooter({ prev, next }: { prev?: DocPage; next?: DocPage }) {
  return (
    <nav className="mt-12 pt-6 border-t border-gray-200 flex justify-between gap-4">
      {prev ? (
        <Link to={prev.path} className="flex-1 block p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors no-underline group">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">← Previous</div>
          <div className="text-sm font-semibold text-gray-800 group-hover:text-blue-700">{prev.title}</div>
        </Link>
      ) : <div className="flex-1"/>}
      {next ? (
        <Link to={next.path} className="flex-1 block p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors no-underline text-right group">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Next →</div>
          <div className="text-sm font-semibold text-gray-800 group-hover:text-blue-700">{next.title}</div>
        </Link>
      ) : <div className="flex-1"/>}
    </nav>
  );
}
